import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDays, monthDay, weekStart, weekdayShort, type ISODate } from '../../shared/dates';
import { prefetchDay, useDays } from '../api/hooks';

interface Props {
  selected: ISODate;
  today: ISODate;
  onSelect: (date: ISODate) => void;
}

export function WeekStrip({ selected, today, onSelect }: Props) {
  const client = useQueryClient();
  const start = weekStart(selected);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const totals = useDays(start, addDays(start, 6));
  const logged = new Set(totals.data?.filter((d) => d.entryCount > 0).map((d) => d.date));
  const nextWeek = addDays(start, 7);

  return (
    <div className="week" role="group" aria-label={`Week of ${monthDay(start)}`}>
      <button className="btn btn--quiet btn--icon btn--sm" aria-label="Previous week" onClick={() => onSelect(addDays(selected, -7))}>
        <ChevronLeft size={18} />
      </button>
      {days.map((d) => {
        const future = d > today;
        return (
          <button
            key={d}
            className={`week__day ${d === selected ? 'is-selected' : ''} ${d === today ? 'is-today' : ''} ${logged.has(d) ? 'has-log' : ''}`}
            disabled={future}
            aria-pressed={d === selected}
            aria-label={`${weekdayShort(d)} ${monthDay(d)}${logged.has(d) ? ', has entries' : ''}`}
            onClick={() => onSelect(d)}
            onPointerEnter={() => void prefetchDay(client, d, today)}
          >
            <span className="week__dow">{weekdayShort(d).slice(0, 2)}</span>
            <span className="week__num num">{Number(d.slice(8))}</span>
            <span className="week__dot" aria-hidden="true" />
          </button>
        );
      })}
      <button
        className="btn btn--quiet btn--icon btn--sm"
        aria-label="Next week"
        disabled={nextWeek > today}
        onClick={() => onSelect(addDays(selected, 7) > today ? today : addDays(selected, 7))}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}
